/**
 * `call-stack`: frames pushed on the way down, and returned on the way back
 * up, which is the half of recursion a static picture cannot show.
 *
 * <div class="pc-widget" data-widget="call-stack"
 *      data-frames='[{"label":"main()","detail":"calls fact(3)"},
 *                    {"label":"fact(3)","detail":"n = 3","returns":"6"},
 *                    {"label":"fact(2)","detail":"n = 2","returns":"2"},
 *                    {"label":"fact(1)","detail":"n = 1","returns":"1"}]'
 *      data-caption="Nothing is multiplied until fact(1) returns.">
 *   <p class="pc-widget-fallback">main calls fact(3), which calls fact(2), which calls fact(1).</p>
 * </div>
 */

import { attachStepper, buildShell, el, parseJson, type WidgetText } from "./dom";

interface Frame {
  label: string;
  detail?: string;
  returns?: string;
}

const isFrames = (value: unknown): value is Frame[] =>
  Array.isArray(value) &&
  value.length > 0 &&
  value.every(
    (frame) => typeof frame === "object" && frame !== null && typeof (frame as Frame).label === "string",
  );

export function renderCallStack(host: HTMLElement, text: WidgetText): boolean {
  const frames = parseJson(host.dataset.frames, isFrames);
  if (!frames) return false;

  const shell = buildShell(host, {
    label: text("labelCallStack"),
    caption: host.dataset.caption,
  });

  const stack = el("div", "pc-stack");
  shell.body.append(stack);
  shell.body.append(el("p", "pc-stack-base", text("stackBase")));

  // Push every frame, then unwind: 2n - 1 steps, so the deepest stack is a
  // step of its own rather than a moment between two others.
  const steps = frames.length * 2 - 1;

  attachStepper(
    shell,
    steps,
    (step) => {
      const pushing = step < frames.length;
      // Pushing: one more frame each step. Unwinding: one fewer, and the frame
      // that just returned is still drawn, dimmed, with its value.
      const depth = pushing ? step + 1 : frames.length * 2 - step;

      stack.textContent = "";
      for (let i = 0; i < depth; i++) {
        const frame = frames[i];
        const node = el("div", "pc-stack-frame");
        const justReturned = !pushing && i === depth - 1;
        if (justReturned) node.classList.add("is-returned");
        else if (i === depth - 1) node.classList.add("is-top");
        node.append(el("span", undefined, frame.label));
        const detail = justReturned && frame.returns ? text("stackReturns", { value: frame.returns }) : (frame.detail ?? "");
        if (detail) node.append(el("span", "pc-stack-detail", detail));
        stack.append(node);
      }
    },
    text,
    {
      stepLabel: (step) =>
        step < frames.length
          ? text("stackPush", { n: step + 1 })
          : text("stackReturn", { n: step - frames.length + 1 }),
    },
  );
  return true;
}
